import { DeleteOutline } from "@mui/icons-material";
import { Collapse, Tooltip } from "@mui/material";
import EXTMarkdown from "components/common/EXTMarkdown";
import MarkdownFieldForm from "components/playbook/CustomForm/FieldComponent/MarkdownFieldForm";
import useFieldCustomize from "components/playbook/hooks/useFieldCustomize";
import React, { Dispatch, FC, SetStateAction, useEffect } from "react";
import { Control, UseFormSetValue, useForm } from "react-hook-form";
import { FieldState, useStyles } from "../common";

interface MarkdownFieldProps {
  state: FieldState | undefined;
  control: Control;
  setValue: UseFormSetValue<any>;
  setFieldStatelist: React.Dispatch<SetStateAction<FieldState[]>>;
  disabled?: boolean;
  readOnly?: boolean;
  handleInput: (
    value: string,
    setFieldStateList: React.Dispatch<SetStateAction<FieldState[]>>
  ) => void;
  enableValidate: boolean;
  showLimit?: boolean;
}

const MarkdownField: FC<MarkdownFieldProps> = ({
  state,
  control,
  setFieldStatelist,
  readOnly = false,
  handleInput,
  enableValidate = true,
}) => {
  return <EXTMarkdown content={(state?.defaultValue as string) || ""}></EXTMarkdown>;
};

export const ConfigMarkdownComponent: FC<{
  fieldId: string;
  control: Control;
  setValue: UseFormSetValue<any>;
  enableValidate: boolean;
  disabled?: boolean;
}> = ({ fieldId, control, setValue, enableValidate }) => {
  const classes = useStyles();

  const { setFieldStateList, selectField, removeField, isCurrentField, getFieldState } =
    useFieldCustomize();

  const state = getFieldState(fieldId);

  return (
    <Tooltip
      title={
        <DeleteOutline
          className={classes.trashIcon}
          onClick={() => removeField(fieldId)}
        />
      }
      placement={"right-start"}
      classes={{ tooltip: classes.tooltip }}
    >
      <div
        onClick={() => selectField(fieldId)}
        className={isCurrentField(fieldId) ? classes.selectedField : classes.unselectedInnerField}
      >
        <EXTMarkdown content={(state?.defaultValue as string) || ""}></EXTMarkdown>
      </div>
    </Tooltip>
  );
};

export const PlainMarkdownComponent: FC<{
  fieldId: string;
  control: Control;
  setValue: UseFormSetValue<any>;
  enableValidate: boolean;
  stateList: FieldState[];
  setStateList: Dispatch<SetStateAction<FieldState[]>>;
  disabled?: boolean;
}> = ({
  fieldId,
  control,
  setValue,
  enableValidate,
  disabled = false,
  stateList,
  setStateList,
}) => {
  const state = stateList.find((x) => x.id === fieldId);

  return (
    <Collapse
      in={state?.visible ?? true}
      style={{
        width: "100%",
        height: (state?.visible ?? true) ? "auto" : 0,
        overflow: "hidden",
      }}
    >
      <EXTMarkdown content={(state?.defaultValue as string) || ""}></EXTMarkdown>
    </Collapse>
  );
};

export const MarkdownPropertyForm: FC<{ fieldId: string }> = ({ fieldId }) => {
  const { setFieldStateList, getFieldState } = useFieldCustomize();
  const state = getFieldState(fieldId);

  const { control, handleSubmit, setValue, unregister } = useForm({
    mode: "all",
  });

  const onSubmit = () => {
    // empty funciton
    setFieldStateList((items) =>
      items.map((item) => (item.id === state?.id ? { ...item, passValidate: true } : item))
    );
  };

  const onError = () => {
    // empty function
  };

  useEffect(() => {
    setValue("labelName", state?.label.value);
    state?.conditions?.map((c) => {
      setValue(c.ruleId, c.fieldId);
      setValue(`${c.ruleId}-values`, c.fieldValue);
    });
    handleSubmit(onSubmit, onError)();
  }, [state?.conditions, state?.label, state?.rules]);

  return (
    <form onSubmit={handleSubmit(onSubmit, onError)}>
      <MarkdownFieldForm
        formHeader="Markdown"
        fieldId={fieldId}
        control={control}
        setValue={setValue}
        unregister={unregister}
        specComponent={<></>}
      />
    </form>
  );
};

export const MarkdownViewComponent: FC<{ state: FieldState }> = ({ state }) => {
  return (
    <>
      {(state?.visible ?? true) && (
        <div
          key={state.id}
          style={{
            width: "100%",
            lineHeight: "32px",
            marginTop: 8,
          }}
        >
          <EXTMarkdown content={(state.defaultValue as string) || ""}></EXTMarkdown>
        </div>
      )}
    </>
  );
};
